// frontend-react/src/components/RuleManagement/RulePreviewModal.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, message, Tabs } from 'antd';
import { EyeOutlined, DownloadOutlined, BarChartOutlined, BulbOutlined } from '@ant-design/icons';
import { RuleRow, RuleSet } from '../../types/rule';
import {
  loadRuleSetDetail,
  loadRuleSetRawData,
  loadRuleSetRiskLevels,
  getCompanyRiskLevels,
  getCompanyComparisonRiskLevels,
  updateRiskLevelsConfig,
  updateRuleSetContent,
  updateRuleSetName,
  downloadRuleSetDocx,
} from '../../services/ruleService';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import RuleSetViewPanel from './RuleSetViewPanel';
import RuleEditAnalysisTab from './RuleEditAnalysisTab';
import RuleAIOptimizeTab from './RuleAIOptimizeTab';

interface RulePreviewModalProps {
  visible: boolean;
  ruleSet: RuleSet | null;
  onClose: () => void;
  onRuleSetNameChange?: (id: number, newName: string) => void;
  isComparisonPage?: boolean;
}

const RulePreviewModal: React.FC<RulePreviewModalProps> = ({
  visible,
  ruleSet,
  onClose,
  onRuleSetNameChange,
  isComparisonPage = false,
}) => {
  const { user } = useAuth();
  const { canEditReviewCards } = useFeatureFlags();

  const [localName, setLocalName] = useState('');
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [rawRules, setRawRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('rules');

  // inline 規則編輯
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [savingRow, setSavingRow] = useState(false);

  // 風險等級
  const [riskLevels, setRiskLevels] = useState<Record<string, string>>({});
  const [companyDefaults, setCompanyDefaults] = useState<Record<string, string>>({});

  // 權限
  const isAdmin = !!(user?.roles?.indexOf('ADMIN') !== -1 || user?.is_superadmin);
  const isCreator = !!user && !!ruleSet && Number(user.id) === ruleSet.created_by;
  const canUploadRules = !!user?.permissions?.can_upload_rules;
  const canEdit = isAdmin || (isCreator && canUploadRules);

  useEffect(() => {
    if (visible && ruleSet) {
      setLocalName(ruleSet.rule_set_name);
      loadRuleDetail();
    } else {
      setLocalName('');
      setRules([]);
      setRawRules([]);
      setExpandedRows([]);
      setRiskLevels({});
      setCompanyDefaults({});
      setEditingRowKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, ruleSet]);

  const loadRuleDetail = async () => {
    if (!ruleSet) return;
    setLoading(true);
    try {
      const [ruleData, rawData, customRisk, companyDefaultLevels] = await Promise.all([
        loadRuleSetDetail(ruleSet.id),
        loadRuleSetRawData(ruleSet.id),
        loadRuleSetRiskLevels(ruleSet.id),
        isComparisonPage ? getCompanyComparisonRiskLevels() : getCompanyRiskLevels(),
      ]);
      setRules(ruleData);
      setRawRules(rawData);
      setCompanyDefaults(companyDefaultLevels);
      setRiskLevels(customRisk ?? companyDefaultLevels);
      if (ruleData.length === 0) message.warning('此規則集暫無規則內容');
    } catch {
      message.error('載入規則詳情失敗');
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  // ── 名稱編輯 ──
  const handleNameChange = async (newName: string) => {
    if (!ruleSet) return;
    const result = await updateRuleSetName(ruleSet.id, newName);
    if (result.success) {
      setLocalName(newName);
      onRuleSetNameChange?.(ruleSet.id, newName);
      message.success('規則集名稱已更新');
    } else {
      message.error(result.error ?? '更新失敗');
    }
  };

  // ── inline 規則編輯 ──
  const handleSaveRow = async (key: string, name: string, content: string) => {
    if (!ruleSet) return;
    setSavingRow(true);
    try {
      const row = rules.find(r => r.key === key);
      const rowId = row?.序號 ?? '';
      const updatedRaw =
        rawRules.length > 0
          ? rawRules.map(r =>
              String(r.id ?? r['序號'] ?? '') === rowId
                ? { ...r, name, original_text: content, current_version: 'original' }
                : r
            )
          : rules.map(r => ({
              id: r.序號,
              name: r.key === key ? name : r.規則名稱,
              original_text: r.key === key ? content : r.規則內容,
              ai_text: '',
              current_version: 'original',
            }));
      const result = await updateRuleSetContent(ruleSet.id, updatedRaw);
      if (result.success) {
        setRules(prev =>
          prev.map(r => (r.key === key ? { ...r, 規則名稱: name, 規則內容: content } : r))
        );
        setRawRules(updatedRaw);
        setEditingRowKey(null);
        message.success('規則已更新');
      } else {
        message.error(result.error ?? '儲存失敗');
      }
    } finally {
      setSavingRow(false);
    }
  };

  const handleDeleteRow = async (key: string) => {
    if (!ruleSet) return;
    setSavingRow(true);
    try {
      const deletedRow = rules.find(r => r.key === key);
      const rowId = deletedRow?.序號 ?? '';
      const newRaw = rawRules
        .filter(r => String(r.id ?? r['序號'] ?? '') !== rowId)
        .map((r, idx) => ({ ...r, id: String(idx + 1) }));
      const result = await updateRuleSetContent(ruleSet.id, newRaw);
      if (result.success) {
        setRules(prev =>
          prev.filter(r => r.key !== key).map((r, idx) => ({ ...r, 序號: String(idx + 1) }))
        );
        setRawRules(newRaw);
        setEditingRowKey(null);
        message.success('規則已刪除');
      } else {
        message.error(result.error ?? '刪除失敗');
      }
    } finally {
      setSavingRow(false);
    }
  };

  // ── 風險等級儲存（交給 Panel 的 callback）──
  const handleSaveRiskLevels = async (levels: Record<string, string> | null) => {
    if (!ruleSet) return { success: false, error: '規則集不存在' };
    const result = await updateRiskLevelsConfig(ruleSet.id, levels);
    if (result.success) {
      setRiskLevels(levels ?? { ...companyDefaults });
      message.success(levels === null ? '已恢復使用公司預設風險等級' : '風險等級定義已儲存');
    } else {
      message.error(result.error ?? '儲存失敗');
    }
    return result;
  };

  // ── 下載 ──
  const handleDownloadCsv = () => {
    if (!ruleSet || rules.length === 0) return;
    const escape = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const lines = [
      ['項次', '規則名稱', '規則內容'].map(escape).join(','),
      ...rules.map(r => [r.序號, r.規則名稱, r.規則內容].map(escape).join(',')),
    ];
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rules_${ruleSet.rule_set_name}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadDocx = async () => {
    if (!ruleSet || rules.length === 0) return;
    setDownloading(true);
    try {
      await downloadRuleSetDocx(ruleSet.id, ruleSet.rule_set_name);
    } finally {
      setDownloading(false);
    }
  };

  const tabItems = [
    {
      key: 'rules',
      label: (
        <span>
          <EyeOutlined />
          規則列表
        </span>
      ),
      children: ruleSet ? (
        <RuleSetViewPanel
          name={localName}
          nameEditable={canEdit}
          onNameChange={handleNameChange}
          subtext={
            ruleSet.created_by_name
              ? `上傳者：${ruleSet.created_by_name}${ruleSet.created_by_department ? ` (${ruleSet.created_by_department})` : ''}`
              : undefined
          }
          loading={loading}
          rules={rules}
          expandedRows={expandedRows}
          onExpandChange={setExpandedRows}
          editMode={canEdit && !ruleSet.content_locked}
          editingRowKey={editingRowKey}
          savingRow={savingRow}
          onEditRow={setEditingRowKey}
          onCancelEdit={() => setEditingRowKey(null)}
          onSaveRow={handleSaveRow}
          onDeleteRow={handleDeleteRow}
          showRisk
          riskLevels={riskLevels}
          defaultRiskLevels={companyDefaults}
          canEditRisk={canEdit}
          onSaveRiskLevels={handleSaveRiskLevels}
        />
      ) : null,
    },
    ...(canEditReviewCards && ruleSet
      ? [
          {
            key: 'edit-analysis',
            label: (
              <span>
                <BarChartOutlined />
                編輯紀錄
              </span>
            ),
            children: <RuleEditAnalysisTab ruleSetId={ruleSet.id} />,
          },
          {
            key: 'ai-optimize',
            label: (
              <span>
                <BulbOutlined />
                AI 分析優化
              </span>
            ),
            children: <RuleAIOptimizeTab ruleSetId={ruleSet.id} onApplied={loadRuleDetail} />,
          },
        ]
      : []),
  ];

  return (
    <Modal
      title={
        <span>
          <EyeOutlined style={{ marginRight: 8 }} />
          查看規則集
        </span>
      }
      open={visible}
      onCancel={onClose}
      footer={
        activeTab === 'rules' ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadCsv}
              disabled={loading || rules.length === 0}
            >
              CSV
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadDocx}
              loading={downloading}
              disabled={loading || rules.length === 0}
            >
              Word
            </Button>
          </div>
        ) : null
      }
      width='80vw'
      destroyOnClose
      centered={false}
      style={{ top: 40 }}
      styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflow: 'auto', padding: '10px 3px' } }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ marginTop: -8 }}
      />
    </Modal>
  );
};

export default RulePreviewModal;
